import type {
  AgentSdkMessage,
  ContentBlock,
  InboundScreeningResult,
  ResponsesInputMessage,
} from "@forgepoint/shared";
import {
  SEND_RICH_MESSAGE_TOOL_NAME,
  ScreeningVerdictEnum,
} from "@forgepoint/shared";
import {
  extractToolCallError,
  isEmailSendAction,
  parseRichActionType,
  type RichActionType,
} from "@/modules/agents/components/helpers/richActionHelpers";

export const SEND_MESSAGE_TO_THREAD_TOOL_NAME = "send_message_to_thread";
export const WAIT_ACTION_TOOL_NAME = "wait_action";

export interface MessageNode {
  message: AgentSdkMessage;
  children: MessageNode[];
  toolCallId?: string;
  toolResult?: AgentSdkMessage;
}

export function extractTextFromContent(
  content: string | ContentBlock[] | undefined,
): string {
  if (!content) {
    return "";
  }
  if (typeof content === "string") {
    return content;
  }
  if (!Array.isArray(content)) {
    return "";
  }
  return content
    .filter((part) => part.type === "text")
    .map((part) => part.text || "")
    .join("\n");
}

export function extractMessageContent(message: ResponsesInputMessage): string {
  if (message.type === "compaction_summary") {
    return message.content;
  }
  if (message.type === "message" && "content" in message) {
    return extractTextFromContent(message.content);
  }
  if (message.type === "reasoning" && "content" in message) {
    return message.content.map((c) => c.text).join("\n");
  }
  return "";
}

export function isCompactionSummaryMessage(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "compaction_summary";
  content: string;
} {
  return message.type === "compaction_summary";
}

export function isInputOutputMessage(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "message";
  role: "user" | "system" | "developer" | "assistant";
  content: string | ContentBlock[];
} {
  return message.type === "message" && "role" in message;
}

export function isToolCallMessage(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "tool_call";
  call_id: string;
  name: string;
  arguments: Record<string, unknown>;
} {
  return message.type === "tool_call";
}

export function isToolCallResultMessage(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "tool_call_result";
  call_id: string;
  name: string;
  result: string | ContentBlock[];
} {
  return message.type === "tool_call_result";
}

export function isReasoningMessage(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "reasoning";
  content: { text: string }[];
} {
  return message.type === "reasoning";
}

export function extractToolCallId(
  message: ResponsesInputMessage,
): string | null {
  if (isToolCallMessage(message)) {
    return message.call_id;
  }
  return null;
}

export function isTaskToolCall(name: string): boolean {
  return name === "Agent" || name === "task";
}

/** Detects whether a message is a send_rich_message tool call. */
export function isSendRichMessageToolCall(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "tool_call";
  call_id: string;
  name: string;
  arguments: Record<string, unknown>;
} {
  return (
    isToolCallMessage(message) && message.name === SEND_RICH_MESSAGE_TOOL_NAME
  );
}

/** Detects whether a message is a send_message_to_thread tool call. */
export function isSendMessageToThreadToolCall(
  message: ResponsesInputMessage,
): message is ResponsesInputMessage & {
  type: "tool_call";
  call_id: string;
  name: string;
  arguments: Record<string, unknown>;
} {
  return (
    isToolCallMessage(message) &&
    message.name === SEND_MESSAGE_TO_THREAD_TOOL_NAME
  );
}

export function getRichActionType(
  message: ResponsesInputMessage,
): RichActionType | undefined {
  if (!isToolCallMessage(message)) {
    return undefined;
  }
  return parseRichActionType(message.name);
}

function buildToolResultMap(
  messages: AgentSdkMessage[],
): Map<string, AgentSdkMessage> {
  const resultMap = new Map<string, AgentSdkMessage>();
  for (const msg of messages) {
    if (isToolCallResultMessage(msg.message)) {
      resultMap.set(msg.message.call_id, msg);
    }
  }
  return resultMap;
}

export function buildMessageTree(messages: AgentSdkMessage[]): MessageNode[] {
  const messageMap = new Map<string, MessageNode>();
  const nodeByToolCallId = new Map<string, MessageNode>();
  const rootNodes: MessageNode[] = [];
  const toolResultMap = buildToolResultMap(messages);

  for (const msg of messages) {
    if (isEmptyAssistantMessage(msg) || isEmptyReasoningMessage(msg)) {
      continue;
    }

    if (isToolCallResultMessage(msg.message)) {
      continue;
    }

    const toolCallId = extractToolCallId(msg.message);
    const toolResult = toolCallId ? toolResultMap.get(toolCallId) : undefined;

    const node: MessageNode = {
      message: msg,
      children: [],
      toolCallId: toolCallId ?? undefined,
      toolResult,
    };
    messageMap.set(msg.id, node);
    if (toolCallId) {
      nodeByToolCallId.set(toolCallId, node);
    }
  }

  for (const [, node] of messageMap) {
    const parentToolCallId = node.message.parentToolCallId;
    const parent =
      parentToolCallId !== null
        ? nodeByToolCallId.get(parentToolCallId)
        : undefined;

    if (parent) {
      parent.children.push(node);
    } else {
      rootNodes.push(node);
    }
  }

  const sortByDate = (nodes: MessageNode[]) => {
    nodes.sort(
      (a, b) => a.message.createdAt.getTime() - b.message.createdAt.getTime(),
    );
    for (const node of nodes) {
      if (node.children.length > 0) {
        sortByDate(node.children);
      }
    }
  };

  sortByDate(rootNodes);

  return rootNodes;
}

function isEmptyAssistantMessage(message: AgentSdkMessage): boolean {
  if (!isInputOutputMessage(message.message)) {
    return false;
  }

  if (message.message.role !== "assistant") {
    return false;
  }

  const hasAssets = (message.assets?.length ?? 0) > 0;
  if (hasAssets) {
    return false;
  }

  return extractTextFromContent(message.message.content).trim().length === 0;
}

/**
 * Checks if a message is a user-role message (type "message", role "user").
 * @param msg - The agent SDK message to check
 * @returns Whether the message is a user message
 */
export function isUserSdkMessage(msg: AgentSdkMessage): boolean {
  return msg.message.type === "message" && msg.message.role === "user";
}

/**
 * Checks if a message is a user message with a FLAG or BLOCK screening verdict.
 * @param msg - The agent SDK message to check
 * @returns Whether the message has a screening hit
 */
export function isScreeningHitMessage(msg: AgentSdkMessage): boolean {
  return (
    isUserSdkMessage(msg) &&
    (msg.messageMetadata?.screeningResult?.verdict ===
      ScreeningVerdictEnum.FLAG ||
      msg.messageMetadata?.screeningResult?.verdict ===
        ScreeningVerdictEnum.BLOCK)
  );
}

/**
 * Finds the user message pending screening review in a thread.
 * Returns the first user message with a PENDING_REVIEW task, but only
 * if the thread contains at least one screening hit (FLAG or BLOCK).
 * @param messages - All messages in the thread
 * @returns The pending screening message, or undefined if none
 */
export function findPendingScreeningMessage(
  messages: AgentSdkMessage[],
): AgentSdkMessage | undefined {
  if (!messages.some(isScreeningHitMessage)) {
    return undefined;
  }
  return messages.find(
    (msg) => isUserSdkMessage(msg) && msg.task?.status === "PENDING_REVIEW",
  );
}

/**
 * Returns the screening result from the first hit message in the thread.
 * @param messages - All messages in the thread
 * @returns The screening result, or null if no screening hit exists
 */
export function getThreadScreeningResult(
  messages: AgentSdkMessage[],
): InboundScreeningResult | null {
  const hitMsg = messages.find(isScreeningHitMessage);
  return hitMsg?.messageMetadata?.screeningResult ?? null;
}

/** A top-level item in the thread stream: a conversation turn or a compaction marker. */
export type ThreadStreamItem =
  | { type: "turn"; turn: ThreadTurn }
  | { type: "compaction"; node: MessageNode };

/**
 * One conversation turn: the user message that started it, the agent work that
 * followed (folded into the timeline), rich actions rendered as banner cards
 * (popped out of the timeline), messages moved to other threads (rendered as
 * cards under the reply), and, for closed turns, the agent's final reply.
 */
export interface ThreadTurn {
  id: string;
  userMessage?: MessageNode;
  timelineItems: MessageNode[];
  bannerActions: MessageNode[];
  movedMessages: MessageNode[];
  finalMessage?: MessageNode;
  isOpen: boolean;
}

/**
 * Segments the message tree into conversation turns. A turn starts at each
 * top-level user message; everything until the next user message is that
 * turn's agent work. Banner rich actions and completed thread moves are split
 * out so the view can render them as cards outside the collapsed timeline, and
 * the turn's stamped reply is extracted as its visible message. A turn with no
 * reply yet stays open, so all work streams into the timeline until the run
 * that closes it stamps one.
 * @param nodes - The thread's top-level message tree
 * @param isThreadActive - Whether the thread is currently running
 * @returns The thread stream: turns and compaction markers in order
 */
export function groupIntoTurns(
  nodes: MessageNode[],
  isThreadActive: boolean,
): ThreadStreamItem[] {
  const items: ThreadStreamItem[] = [];
  const turns: ThreadTurn[] = [];
  let currentTurn: ThreadTurn | null = null;

  // Headerless turns (page starts mid-turn) key off their first message so the
  // id survives older pages prepending; an index would shift and remount it.
  const startTurn = (userMessage?: MessageNode, fallbackId?: string) => {
    const turn: ThreadTurn = {
      id: userMessage?.message.id ?? `turn-start-${fallbackId}`,
      userMessage,
      timelineItems: [],
      bannerActions: [],
      movedMessages: [],
      isOpen: false,
    };
    turns.push(turn);
    items.push({ type: "turn", turn });
    currentTurn = turn;
    return turn;
  };

  for (const node of nodes) {
    const msg = node.message.message;

    if (isCompactionSummaryMessage(msg)) {
      items.push({ type: "compaction", node });
      currentTurn = null;
      continue;
    }

    if (isInputOutputMessage(msg) && msg.role === "user") {
      startTurn(node);
      continue;
    }

    if (!isTimelineVisibleMessage(msg)) {
      continue;
    }

    const turn = currentTurn ?? startTurn(undefined, node.message.id);
    if (isBannerRichAction(node)) {
      turn.bannerActions.push(node);
      continue;
    }
    if (isCompletedThreadMove(node)) {
      turn.movedMessages.push(node);
      continue;
    }
    turn.timelineItems.push(node);
  }

  for (const turn of turns) {
    const isLiveTurn = isThreadActive && turn === turns[turns.length - 1];
    turn.finalMessage = takeFinalMessage(turn, isLiveTurn);
    turn.isOpen = isLiveTurn && turn.finalMessage === undefined;
  }

  return items;
}

/**
 * Whether a rich action renders as a banner card outside the timeline fold.
 * Email sends fold into the timeline once resolved (banner only while pending
 * review); every other rich action keeps its banner card in the stream
 * regardless of status.
 */
function isBannerRichAction(node: MessageNode): boolean {
  const actionType = getRichActionType(node.message.message);
  if (!actionType) {
    return false;
  }
  if (!isEmailSendAction(actionType)) {
    return true;
  }
  return node.message.task?.status === "PENDING_REVIEW";
}

/**
 * Whether a node is a send_message_to_thread call that succeeded. In-flight and
 * failed moves stay in the timeline so their progress and failure show there.
 * @param node - The timeline node to check
 * @returns True when the message was moved to another thread
 */
function isCompletedThreadMove(node: MessageNode): boolean {
  return (
    isSendMessageToThreadToolCall(node.message.message) &&
    node.toolResult !== undefined &&
    !extractToolCallError(node.toolResult)
  );
}

/** Whether a turn message renders as a timeline step at all. */
function isTimelineVisibleMessage(msg: ResponsesInputMessage): boolean {
  if (isToolCallMessage(msg) && msg.name === WAIT_ACTION_TOOL_NAME) {
    return false;
  }
  if (isInputOutputMessage(msg)) {
    return msg.role === "assistant";
  }
  return isToolCallMessage(msg) || isReasoningMessage(msg);
}

/** Removes the turn's visible reply from the timeline and returns it. */
function takeFinalMessage(
  turn: ThreadTurn,
  isLiveTurn: boolean,
): MessageNode | undefined {
  const index = findFinalMessageIndex(turn.timelineItems, isLiveTurn);
  if (index === -1) {
    return undefined;
  }
  const [finalMessage] = turn.timelineItems.splice(index, 1);
  return finalMessage;
}

/**
 * Locates the turn's visible reply. A live turn trusts only the backend's
 * stamp; a settled turn falls back to its trailing agent message, for threads
 * written before the stamp existed.
 */
function findFinalMessageIndex(
  items: MessageNode[],
  isLiveTurn: boolean,
): number {
  const stamped = items.reduce(
    (found, node, index) => (node.message.isTurnFinal ? index : found),
    -1,
  );
  if (stamped !== -1) {
    // The stamp lands on a send_rich_message call even when the send failed.
    return isConversationMessage(items[stamped]) ? stamped : -1;
  }
  if (isLiveTurn) {
    return -1;
  }
  const last = items.length - 1;
  return last >= 0 && isConversationMessage(items[last]) ? last : -1;
}

/** Whether a node reads as a message to the user (plain or rich, not errored). */
export function isConversationMessage(node: MessageNode): boolean {
  const msg = node.message.message;
  if (isInputOutputMessage(msg) && msg.role === "assistant") {
    return true;
  }
  return (
    isSendRichMessageToolCall(msg) &&
    node.toolResult !== undefined &&
    !extractToolCallError(node.toolResult)
  );
}

/**
 * Whether a timeline node folds into a work group — the technical steps the
 * agent takes: reasoning, tool calls, sub-agent tasks, and routed messages.
 * Prose messages and rich actions keep their own top-level row.
 */
export function isFoldableStepNode(node: MessageNode): boolean {
  const msg = node.message.message;
  if (isReasoningMessage(msg)) {
    return true;
  }
  if (!isToolCallMessage(msg)) {
    return false;
  }
  if (
    msg.name === WAIT_ACTION_TOOL_NAME ||
    msg.name === SEND_RICH_MESSAGE_TOOL_NAME
  ) {
    return false;
  }
  return getRichActionType(msg) === undefined;
}

/** Whether a tool call is still running: the thread is live and no result landed. */
export function isToolCallExecuting(
  node: MessageNode,
  isThreadActive: boolean,
): boolean {
  return (
    isThreadActive &&
    isToolCallMessage(node.message.message) &&
    !node.toolResult &&
    !node.message.task
  );
}

/**
 * Whether a timeline node renders as flat prose on the working layer: an
 * intermediate agent message (plain or send_rich_message), shown in full
 * rather than folded behind a row.
 */
export function isProseMessageNode(node: MessageNode): boolean {
  const msg = node.message.message;
  return isInputOutputMessage(msg) || isSendRichMessageToolCall(msg);
}

/**
 * One rendered timeline row: flat message prose, a single rail step, or a
 * folded run of the agent's technical work.
 */
export type TimelineDisplayItem =
  | { type: "message"; node: MessageNode }
  | { type: "step"; node: MessageNode }
  | { type: "workGroup"; nodes: MessageNode[] };

/**
 * Splits timeline nodes into display rows. Agent messages surface as flat
 * prose items, consecutive technical steps fold into one group so a run of
 * them reads as a single "Agent worked" row, and every other step keeps its
 * own row. Order is preserved and a prose message or rich action splits a run,
 * so work either side of one yields two groups.
 */
export function groupTimelineDisplayItems(
  nodes: MessageNode[],
): TimelineDisplayItem[] {
  const items: TimelineDisplayItem[] = [];
  for (const node of nodes) {
    if (isProseMessageNode(node)) {
      items.push({ type: "message", node });
      continue;
    }
    if (!isFoldableStepNode(node)) {
      items.push({ type: "step", node });
      continue;
    }
    const previous = items[items.length - 1];
    if (previous && previous.type === "workGroup") {
      previous.nodes.push(node);
      continue;
    }
    items.push({ type: "workGroup", nodes: [node] });
  }
  return items;
}

/** Stable React key for a display item, keyed off its first message. */
export function getTimelineDisplayItemKey(item: TimelineDisplayItem): string {
  const node = item.type === "workGroup" ? item.nodes[0] : item.node;
  return node.message.id;
}

/** Expand/collapse key for a single step row. */
export function getTimelineStepSectionId(node: MessageNode): string {
  return `timeline-step-${node.message.id}`;
}

/** Expand/collapse key for a folded work group row. */
export function getWorkGroupSectionId(nodes: MessageNode[]): string {
  return `timeline-work-group-${nodes[0].message.id}`;
}

/** Whether a rich action step is hidden because it was cancelled. */
export function isHiddenCancelledAction(
  node: MessageNode,
  showCancelled: boolean,
): boolean {
  if (showCancelled) {
    return false;
  }
  return (
    node.message.task?.status === "CANCELLED" &&
    getRichActionType(node.message.message) !== undefined
  );
}

/** Timeline step count shown on the header, excluding hidden cancelled actions. */
export function getVisibleStepCount(
  turn: ThreadTurn,
  showCancelled: boolean,
): number {
  return turn.timelineItems.filter(
    (node) => !isHiddenCancelledAction(node, showCancelled),
  ).length;
}

/** Formats a step count like "1 step" or "14 steps". */
export function formatStepCount(count: number): string {
  return `${count} ${count === 1 ? "step" : "steps"}`;
}

/** Formats a turn duration like "45s" or "3m" for the timeline header. */
export function formatTurnDuration(durationMs: number): string {
  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) {
    return `${Math.max(seconds, 1)}s`;
  }
  return `${Math.round(seconds / 60)}m`;
}

/**
 * Millisecond span from the first to the last step of a turn.
 * @param turn - The conversation turn
 * @returns The span in milliseconds, or null when the turn has fewer than two stamps
 */
export function getTurnDurationMs(turn: ThreadTurn): number | null {
  const times = [
    ...turn.timelineItems,
    ...turn.bannerActions,
    ...turn.movedMessages,
    ...(turn.finalMessage ? [turn.finalMessage] : []),
  ].flatMap((node) => {
    const stamps = [node.message.createdAt.getTime()];
    if (node.toolResult) {
      stamps.push(node.toolResult.createdAt.getTime());
    }
    return stamps;
  });
  if (turn.userMessage) {
    times.unshift(turn.userMessage.message.createdAt.getTime());
  }
  if (times.length < 2) {
    return null;
  }
  return Math.max(...times) - Math.min(...times);
}

// OpenAI reasoning items may carry only encrypted content with no summary text
function isEmptyReasoningMessage(message: AgentSdkMessage): boolean {
  if (!isReasoningMessage(message.message)) {
    return false;
  }
  return extractMessageContent(message.message).trim().length === 0;
}
